import { FabrixIcon, formatDate, updateTheme } from "fabrix/common";
import { useCurrentUser, useIcon, useSettings, useShowMessage, useTranslation, useUserProfile, useUserRoles } from "fabrix/hooks";
import { resetAppState } from "fabrix/redux/store";
import { JwtAuthService, UserCache } from "fabrix/services";
import { Button } from "primereact/button";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { ClientUserService } from "../../services/modules/core";
import { UserProfileDialog } from "./user-profile.dialog";

export interface UserLoginClick {
  (e: any | undefined): void;
}

export interface UserProfileComponentProps {
  onLogout: UserLoginClick;
  onClose: () => void;
}

/**
 * Displays the active user's profile info with logout controls and token expiry details for admins.
 * @param props - Callbacks used when closing or logging out via the profile panel.
 * @returns A styled profile card summarizing user details and actions.
 */
export default function UserProfileComponent(props: UserProfileComponentProps) {

  const translate = useTranslation();
  const _currentUser = useCurrentUser();
  const [_userProfile, setUserProfile] = useUserProfile();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [_confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const icon = useIcon();
  const _roles = useUserRoles();
  const showMessage = useShowMessage();
  const [_tokenDate, setTokenDate] = useState<Date | null>(null);
  const settings = useSettings();
  const [_profileOpen, setProfileOpen] = useState<boolean>(false);

  /**
   * Signs the current user out, clears cached state, and redirects to the sign-in view.
   * @param e - Optional event payload from the triggering UI element.
   */
  const logout = (e: any) => {
    let authService = new JwtAuthService();
    authService.logout().then(() => {
      UserCache.clear();
      dispatch(resetAppState());
      props.onLogout(e);
      navigate("/signin");
    });
  };

  let name = ClientUserService.displayName(_currentUser);
  let email = _currentUser?.email;

  useEffect(() => {
    if (!settings.production || (_currentUser && (_roles?.includes("admin") || _roles?.includes("system-admin")))) {
      setTokenDate(JwtAuthService.tokenExpiry())
    }
  }, [_currentUser]);

  return (
    <div
      className="user-profile-container"
      style={{
        width: "100%",
        border: "1px solid #ddd",
        padding: 20,
        backgroundColor: "white",
        borderRadius: 10,
        boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
      }}
    >
      <div className="profile-header mb-3">
        <h3
          style={{
            margin: 0,
            color: "#333",
            borderBottom: "1px solid #eee",
            paddingBottom: "10px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <i className="pi pi-user mr-2" style={{ color: "#FBC02D" }}></i>
          {translate("USER_PROFILE") || "User Profile"}
        </h3>
      </div>

      <div className="profile-content p-3" style={{ backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
        <div className="user-avatar mb-3 text-center" style={{ cursor: "pointer" }} onClick={() => setProfileOpen(true)}>
          <div 
            style={{
              backgroundColor: "#FFF8E1",
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              margin: "0 auto",
              border: "2px solid #FFECB3",
            }}
          >
            
            <i className="pi pi-user" style={{ fontSize: "2.5rem", color: "#FBC02D" }}></i>
          </div>
        </div>

        <div className="user-details text-center mb-3">
          <h4 className="user-name m-0 text-xl font-bold" title={name} style={{ color: "#333" }}>
            {name}
          </h4>
          <p className="user-email text-sm text-500" title={email} style={{ margin: "5px 0 0 0" }}>
            {email}
          </p>
        </div>

        <div
          className="divider my-3"
          style={{
            borderBottom: "1px solid #eee",
            margin: "15px 0",
          }}
        ></div>



        <div className="actions-section flex-column flex justify-content-center pt-2">
          <Button icon={icon(FabrixIcon.logout)} label={translate("LOGOUT") || "Logout"} className="p-button-rounded p-button-outlined" onClick={logout} style={{ borderRadius: "20px" }} tooltip={translate("LOGOUT_TOOLTIP") || "Sign out of your account"} tooltipOptions={{ position: "top" }} />

          {_tokenDate && (
            <div style={{ marginTop: 10, fontSize: 14 }}>{`${translate("TOKEN_EXPIRES_AT")} : ${formatDate(_tokenDate)}`}</div>
          )}

        </div>


      </div>

      {/* <div className="col-12 flex">
                <div className="flex email-notification-label" >{translate("EMAIL_NOTIFICATIONS")}</div>
                <InputSwitch className="flex fbx-input-switch" style={{height: 24}} checked={_currentUser?.profile?.emailNotifications ?? true} onChange={handleEmailNotChanged} /> 
            </div> */}

      {/* <FabrixConfirmDialog {...confirmDialogProps} /> */}
      <UserProfileDialog open={_profileOpen} onClose={() => setProfileOpen(false)} />
    </div>
  );
}
